/**
 * PauseModal — RESPONDENT_WIZARD_SPEC v1.3 §4.3.
 *
 * Modale en 2 étapes :
 *  1. Confirmation d'isolement (« je suis seul·e à voir mon écran »)
 *  2. Affichage du code de reprise — auto-masquage après copie ou 60s,
 *     limite 2 réaffichages par modale, bouton Régénérer (rotate serveur),
 *     bouton Effacer art. 17 avec sur-validation « EFFACER ».
 *
 * Le bouton « Réafficher » respecte aussi le rate limit serveur (3/jour).
 */
import { useEffect, useState } from 'react';
import {
  getCurrentResumeCode,
  regenerateResumeCode,
  eraseWizardSession,
} from '@/lib/api';

type Step = 'intro' | 'code' | 'erase-confirm' | 'erased' | 'error';

const MAX_REVEALS_PER_MODAL = 2;
const AUTO_MASK_AFTER_MS = 60_000;

export function PauseModal({
  slug,
  resumeToken,
  open,
  onClose,
  onErased,
}: {
  slug: string;
  resumeToken: string | null;
  open: boolean;
  onClose: () => void;
  onErased: () => void;
}): JSX.Element | null {
  const [step, setStep] = useState<Step>('intro');
  const [code, setCode] = useState<string | null>(null);
  const [masked, setMasked] = useState(false);
  const [revealCount, setRevealCount] = useState(0);
  const [copyOk, setCopyOk] = useState(false);
  const [eraseInput, setEraseInput] = useState('');
  const [errMsg, setErrMsg] = useState('');

  // Reset à l'ouverture.
  useEffect(() => {
    if (!open) return;
    setStep('intro');
    setCode(null);
    setMasked(false);
    setRevealCount(0);
    setCopyOk(false);
    setEraseInput('');
    setErrMsg('');
  }, [open]);

  // Auto-masquage à 60s après affichage.
  useEffect(() => {
    if (step !== 'code' || masked) return;
    const t = setTimeout(() => setMasked(true), AUTO_MASK_AFTER_MS);
    return () => clearTimeout(t);
  }, [step, masked, code]);

  if (!open) return null;

  async function reveal(): Promise<void> {
    if (revealCount >= MAX_REVEALS_PER_MODAL) {
      setErrMsg('Vous avez atteint le nombre maximum d’affichages pour cette modale (2). Refermez-la et réessayez plus tard si nécessaire.');
      return;
    }
    try {
      const res = await getCurrentResumeCode(slug);
      setCode(res.code);
      setMasked(false);
      setRevealCount((n) => n + 1);
      setStep('code');
      setErrMsg('');
    } catch (e) {
      setErrMsg('Code indisponible (limite serveur atteinte ou session expirée).');
      setStep('error');
    }
  }

  async function regen(): Promise<void> {
    try {
      const res = await regenerateResumeCode(slug);
      setCode(res.code);
      setMasked(false);
      setCopyOk(false);
      setErrMsg('');
    } catch {
      setErrMsg('Régénération impossible.');
    }
  }

  async function copy(): Promise<void> {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopyOk(true);
      // Auto-masque dès la copie.
      setMasked(true);
    } catch {
      setErrMsg('Copie impossible.');
    }
  }

  async function doErase(): Promise<void> {
    if (eraseInput.trim() !== 'EFFACER' || !resumeToken) {
      setErrMsg('Saisissez EFFACER pour confirmer.');
      return;
    }
    try {
      await eraseWizardSession(slug, resumeToken);
      setStep('erased');
      onErased();
    } catch {
      setErrMsg('Effacement impossible côté serveur.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Pause — code de reprise"
      onClick={onClose}
    >
      <div className="card max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
        {step === 'intro' && (
          <>
            <h2 className="text-lg font-semibold">Votre code de reprise va s’afficher</h2>
            <p className="text-sm text-text-muted">
              Assurez-vous que :
            </p>
            <ul className="text-sm space-y-1 list-disc pl-5">
              <li>personne ne regarde votre écran ;</li>
              <li>vous ne partagez pas votre écran ;</li>
              <li>aucun enregistrement d’écran n’est en cours.</li>
            </ul>
            <p className="text-sm text-text-muted">
              Ce code donne accès à vos réponses partielles. Gardez-le pour vous.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
              <button type="button" className="btn-primary" onClick={reveal}>
                Je suis seul·e, afficher mon code
              </button>
            </div>
          </>
        )}

        {step === 'code' && code && (
          <>
            <h2 className="text-lg font-semibold">Votre code de reprise</h2>
            <div className="rounded-lg bg-surface-2 p-4 text-center">
              <code className="font-mono text-sm break-all">
                {masked ? '•'.repeat(Math.min(43, code.length)) : code}
              </code>
              {masked && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn-ghost text-sm"
                    onClick={() => setMasked(false)}
                    disabled={revealCount >= MAX_REVEALS_PER_MODAL}
                  >
                    Réafficher ({revealCount} / {MAX_REVEALS_PER_MODAL})
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-text-muted">
              Le code se masque automatiquement après copie ou après 60 secondes
              d’inactivité. Si vous le partagez par erreur, utilisez « Régénérer ».
            </p>
            {copyOk && <p className="text-xs text-success-text">Copié.</p>}
            {errMsg && <p className="field-error">{errMsg}</p>}
            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <button type="button" className="btn-ghost" onClick={() => setStep('erase-confirm')}>
                Effacer mes réponses partielles
              </button>
              <button type="button" className="btn-ghost" onClick={regen}>Régénérer mon code</button>
              <button type="button" className="btn-secondary" onClick={copy} disabled={masked}>
                Copier
              </button>
              <button type="button" className="btn-primary" onClick={onClose}>
                Reprendre plus tard
              </button>
            </div>
          </>
        )}

        {step === 'erase-confirm' && (
          <>
            <h2 className="text-lg font-semibold text-danger-text">Effacement irréversible</h2>
            <p className="text-sm">
              Cela supprimera <strong>toutes vos réponses partielles</strong> et la session.
              Pas de récupération possible (droit à l’effacement, art. 17 RGPD).
            </p>
            <label className="block">
              <span className="field-label">Tapez EFFACER pour confirmer</span>
              <input
                type="text"
                className="input-base"
                autoComplete="off"
                value={eraseInput}
                onChange={(e) => setEraseInput(e.target.value)}
              />
            </label>
            {errMsg && <p className="field-error">{errMsg}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setStep('code')}>Annuler</button>
              <button
                type="button"
                className="btn-danger"
                onClick={doErase}
                disabled={eraseInput.trim() !== 'EFFACER'}
              >
                Effacer définitivement
              </button>
            </div>
          </>
        )}

        {step === 'erased' && (
          <>
            <h2 className="text-lg font-semibold">Vos réponses ont été effacées</h2>
            <p className="text-sm text-text-muted">
              Aucune donnée ne subsiste de cette session. Vous pouvez fermer cet onglet.
            </p>
            <div className="flex justify-end pt-2">
              <button type="button" className="btn-primary" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}

        {step === 'error' && (
          <>
            <h2 className="text-lg font-semibold">Code indisponible</h2>
            <p className="text-sm text-text-muted">{errMsg}</p>
            <div className="flex justify-end pt-2">
              <button type="button" className="btn-primary" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
